/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { useLayoutEffect, useRef } from "react"

export interface TableRenderTracking {
  /**
   * Produces the `onRendered` callback for a single table. Called once per
   * table per render of the consuming component, so every call returns a fresh
   * function identity - which is what makes DataTable's `useEffect(..., [onRendered])`
   * re-fire on every consumer render. See the AIDEV-NOTE below.
   */
  readonly createOnRendered: () => () => void
  /**
   * Live read of how many table renders have been counted against the current
   * result set. Not needed by the UI; it is the observability seam that lets
   * the tracking logic itself be asserted on (issue #468) instead of being
   * reimplemented as a model in a test.
   */
  readonly getRenderedCount: () => number
}

// AIDEV-NOTE: This is a behaviour-preserving extraction of the render tracking that
// previously lived inline in Results.tsx. It deliberately reproduces the *buggy*
// semantics described in issue #468 and does not fix them:
//   - `createOnRendered` builds a new closure on every call, so the callback identity
//     handed to DataTable changes on every consumer render, re-firing DataTable's
//     `onRendered` effect even when the result set has not changed.
//   - the version guard reads `versionRef.current` live inside the callback body
//     rather than capturing it when the closure was created, so
//     `callbackVersion !== versionRef.current` compares the ref to itself and can
//     never be true.
// Together these let `renderedCountRef` be pushed past `totalTableCount` for a
// version. Do not "tidy" either of these without fixing #468 deliberately - the
// property test in useTableRenderTracking.property.test.ts pins the current behaviour.
//
// AIDEV-NOTE: (#1458) The requestAnimationFrame dispatch below is only finite because
// Results.tsx passes `uiTables` as `resultSet` and that identity survives the
// setResultsFresh(true) the dispatch produces (ResultsSlice.ts, selectUiTables and
// setResultsFresh). If a store update that is not a new calculation ever yields a new
// `resultSet` again, the layout effect resets the counter, the DataTables re-report,
// and this hook re-dispatches every frame forever. Results.test.tsx counts those
// dispatches across many frames to catch that.
export const useTableRenderTracking = (
  // Identity-compared trigger for "a genuinely new result set arrived"; the value
  // itself is unused, only its identity matters.
  resultSet: unknown,
  totalTableCount: number,
  onAllTablesRendered: () => void
): TableRenderTracking => {
  const versionRef = useRef(0)
  const renderedTablesRef = useRef(0)

  useLayoutEffect(() => {
    // Increment version and reset counter.
    // Runs before the browser has a chance to paint
    // and before the DOM is updated,
    // i.e. before the tables are rendered
    versionRef.current += 1
    renderedTablesRef.current = 0
  }, [resultSet])

  const handleTableRendered = (callbackVersion: number) => {
    // Only count if the callback's version matches the current version.
    // Prevents counting tables from previous result-set renders.
    if (callbackVersion !== versionRef.current) return
    renderedTablesRef.current += 1
    if (renderedTablesRef.current === totalTableCount) {
      requestAnimationFrame(() => {
        onAllTablesRendered()
      })
    }
  }

  return {
    createOnRendered: () => () => {
      handleTableRendered(versionRef.current)
    },
    getRenderedCount: () => renderedTablesRef.current,
  }
}
